import { useState } from "react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import type { TimelineEntry as Entry } from "./hub-timeline";
import { OperationsLink } from "./OperationsLink";
import type { OperationsNavigate } from "./navigation";

export function HubTimeline({
  entries,
  navigate,
}: {
  entries: readonly Entry[];
  navigate: OperationsNavigate;
}) {
  if (!entries.length)
    return (
      <p className="rounded-xl bg-card p-8 text-center text-muted-foreground shadow-card">
        Nothing here yet. Log a call, paste an email, or add the order form.
      </p>
    );
  return (
    <ol className="divide-y divide-muted rounded-xl bg-card shadow-card" aria-label="Timeline">
      {entries.map((entry) => (
        <TimelineRow key={entry.key} entry={entry} navigate={navigate} />
      ))}
    </ol>
  );
}

function TimelineRow({ entry, navigate }: { entry: Entry; navigate: OperationsNavigate }) {
  const [open, setOpen] = useState(false);
  const Icon = entry.icon;
  const body = entry.record?.bodyText;
  return (
    <li className="grid grid-cols-[1.25rem_minmax(0,1fr)] gap-x-3 px-6 py-4">
      <Icon className="mt-1 size-5 text-muted-foreground" aria-hidden />
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-sm font-semibold text-muted-foreground">{entry.label}</span>
          <time
            className="text-sm text-muted-foreground"
            dateTime={new Date(entry.at * 1000).toISOString()}
          >
            {new Date(entry.at * 1000).toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </time>
          {entry.badges.map((badge) => (
            <Badge key={badge} variant="secondary">
              {badge}
            </Badge>
          ))}
        </div>
        <EntryTitle entry={entry} navigate={navigate} />
        {body ? (
          <div>
            {open ? <p className="whitespace-pre-wrap text-muted-foreground">{body}</p> : null}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setOpen(!open);
              }}
            >
              {open ? "Hide" : "Show"}{" "}
              {entry.record?.type === "call" || entry.record?.type === "meeting"
                ? "notes"
                : "details"}
            </Button>
          </div>
        ) : null}
      </div>
    </li>
  );
}

function EntryTitle({ entry, navigate }: { entry: Entry; navigate: OperationsNavigate }) {
  if (entry.href)
    return (
      <OperationsLink
        className="font-semibold text-link hover:underline"
        href={entry.href}
        navigate={navigate}
      >
        {entry.title}
      </OperationsLink>
    );
  if (entry.external)
    return (
      <a
        className="font-semibold text-link hover:underline"
        href={entry.external}
        target="_blank"
        rel="noreferrer"
      >
        {entry.title}
      </a>
    );
  return <p className="font-semibold">{entry.title}</p>;
}
